import React from 'react'

import { FieldEditorPopover } from 'features/admin/fields/FieldEditorPopover'

import { Box } from 'ui/components/Box'
import {
    Dropdown,
    DropdownButton,
    DropdownContent,
    DropdownEndIndicatorToggle,
    DropdownFooterItem,
    DropdownHeadSearch,
    DropdownItem,
} from 'ui/components/Dropdown'
import { SlotContentProps } from 'ui/components/Dropdown/types'
import { Body } from 'ui/components/Text'
import { Tooltip } from 'ui/components/Tooltip'

import { useToolbarActionsFieldsState } from './hooks/useToolbarActionsFieldsState'

type ToolbarActionsFieldsProps = {}

export const ToolbarActionsFields: React.FC<ToolbarActionsFieldsProps> = () => {
    const {
        objectSid,
        fields,
        toggleFieldState,
        onEnableAll,
        onDisableAll,
        searchQuery,
        setSearchQuery,
        canToggleAll,
        dropdownContentRef,
        addNewButtonRef,
        isFieldEditorOpen,
        onAddNewFieldClick,
        onFieldEditorSuccess,
        onFieldEditorClose,
        isDropdownOpen,
        onDropdownOpenChange,
        allowAddNewField,
        isEmpty,
    } = useToolbarActionsFieldsState()

    return (
        <Dropdown modal={false} open={isDropdownOpen} onOpenChange={onDropdownOpenChange}>
            <DropdownButton size="xs" startIcon={{ name: 'Columns3' }} variant="ghost">
                Fields
            </DropdownButton>
            <DropdownContent
                ref={dropdownContentRef}
                align="end"
                side="bottom"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                head={
                    <DropdownHeadSearch
                        rightSlotContent={
                            <Box
                                flex
                                center
                                noShrink
                                gap="xs"
                                color={canToggleAll ? 'theme600' : 'textDisabled'}
                            >
                                <Body
                                    role={canToggleAll ? 'button' : undefined}
                                    size="s"
                                    weight="medium"
                                    onClick={onEnableAll}
                                    aria-disabled={!canToggleAll}
                                >
                                    enable all
                                </Body>
                                <Body size="s" weight="medium">
                                    /
                                </Body>
                                <Body
                                    role={canToggleAll ? 'button' : undefined}
                                    size="s"
                                    weight="medium"
                                    onClick={onDisableAll}
                                    aria-disabled={!canToggleAll}
                                >
                                    disable all
                                </Body>
                            </Box>
                        }
                    />
                }
                style={{
                    width: '300px',
                }}
                footer={
                    allowAddNewField && (
                        <DropdownFooterItem
                            ref={addNewButtonRef}
                            variant="action"
                            label="Add new field"
                            showSeparator
                            onClick={onAddNewFieldClick}
                            startIcon={{ name: 'Plus' }}
                            closeOnSelect={false}
                        />
                    )
                }
            >
                {isEmpty && (
                    <DropdownItem
                        label={<Body color="textHelper">No fields to show</Body>}
                        disabled
                    />
                )}
                {fields.map((field) => (
                    <DropdownItem
                        key={field.sid}
                        label={field.label}
                        startIcon={field.icon}
                        closeOnSelect={false}
                        multiSelect={false}
                        onClick={() => toggleFieldState(field, !field.isEnabled)}
                        rightSlotContent={(slotProps: SlotContentProps) => (
                            <Tooltip
                                asChild
                                content={field.isEditable ? '' : 'Cannot disable the primary field'}
                                zIndex={9999}
                            >
                                <DropdownEndIndicatorToggle
                                    {...slotProps}
                                    checked={field.isEnabled}
                                    onCheckedChange={(checked: boolean) => {
                                        toggleFieldState(field, checked)
                                    }}
                                    disabled={!field.isEditable}
                                />
                            </Tooltip>
                        )}
                    />
                ))}
                <FieldEditorPopover
                    open={isFieldEditorOpen}
                    placement="left-start"
                    container={dropdownContentRef.current as HTMLElement}
                    target={addNewButtonRef.current as HTMLElement}
                    objectId={objectSid}
                    onSuccess={onFieldEditorSuccess}
                    onCancel={onFieldEditorClose}
                    onClose={onFieldEditorClose}
                    adaptivePlacement={false}
                    zIndex={99999}
                />
            </DropdownContent>
        </Dropdown>
    )
}
